import React from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  Position,
  MarkerType,
  useNodesState,
  useEdgesState,
} from "@xyflow/react";
import type { NodeProps, Node, Edge } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "@dagrejs/dagre";
import { User, Bot, Wrench, GitBranch, AlertTriangle, ShieldX } from "lucide-react";
import { cn } from "../../../lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../ui/dialog";
import { GatewayAuditApi } from "../Api/gatewayApi";
import { apiError } from "../utils/gatewayUtils";
import GatewayConsoleHops from "./GatewayConsoleHops";
import "../../ManagementDialog.css";
import "../AgenticGateway.css";

// Backend TraceGraph shape (GET /api/admin/audit/logs/trace/{id}/graph)
interface TGNode { id: string; type: string; label: string; sublabel?: string | null; level?: number }
interface TGEdge { source: string; target: string; capability?: string | null; decision?: string | null }
interface TGBlocked { from?: string; capability?: string; policyId?: string; reason?: string }
interface TraceGraphData { traceId?: string; nodes?: TGNode[]; edges?: TGEdge[]; blocked?: TGBlocked[] }

const NODE_W = 208;
const NODE_H = 54;

function typeIcon(t: string) {
  if (t === "HUMAN") return <User className="w-4 h-4" />;
  if (t === "TOOL") return <Wrench className="w-4 h-4" />;
  return <Bot className="w-4 h-4" />;
}
function typeIconBg(t: string) {
  if (t === "HUMAN") return "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400";
  if (t === "TOOL") return "bg-teal-500/15 text-teal-600 dark:text-teal-400";
  return "bg-primary/15 text-primary";
}
// Estimate a node's width so dagre spaces the columns to fit each FULL name (no truncation).
function estWidth(label: string): number {
  return Math.round(Math.max(150, 68 + (label || "").length * 9.2)); // ~16px semibold
}

function TraceNodeCard({ data }: NodeProps) {
  const d: any = data;
  return (
    <div
      className={cn(
        "w-full rounded-xl border bg-card shadow-sm px-3 py-2.5 flex items-center gap-2.5",
        d.denied ? "border-red-500/50" : "border-border"
      )}
    >
      <Handle type="target" position={Position.Left} className="!bg-muted-foreground/40 !border-0" />
      <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0", typeIconBg(d.nodeType))}>
        {typeIcon(d.nodeType)}
      </div>
      <div className="min-w-0">
        <div className="text-[16px] font-semibold text-foreground whitespace-nowrap">{d.label}</div>
      </div>
      <Handle type="source" position={Position.Right} className="!bg-muted-foreground/40 !border-0" />
    </div>
  );
}

const nodeTypes = { traceNode: TraceNodeCard };

// dagre layered auto-layout (LR) — ranking, ordering (crossing minimisation) and spacing; branches fan out.
function layoutNodes(nodes: Node[], edges: Edge[]): Node[] {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "LR", nodesep: 34, ranksep: 120, marginx: 24, marginy: 24 });
  nodes.forEach((n) => g.setNode(n.id, { width: ((n.style?.width as number) || NODE_W), height: NODE_H }));
  edges.forEach((e) => g.setEdge(e.source, e.target));
  try {
    dagre.layout(g);
  } catch {
    /* leave nodes stacked at origin rather than crashing */
  }
  return nodes.map((n) => {
    const p: any = g.node(n.id);
    const w = (n.style?.width as number) || NODE_W;
    const x = p && typeof p.x === "number" ? p.x - w / 2 : 0;
    const y = p && typeof p.y === "number" ? p.y - NODE_H / 2 : 0;
    return { ...n, position: { x, y }, sourcePosition: Position.Right, targetPosition: Position.Left } as Node;
  });
}

// Unroll the delegation DAG into a TREE: each call gets its own child instance, so a tool invoked by two agents
// appears once under EACH caller (repeated) instead of merging into one shared node — branches fan out per
// agent, each carrying its own chain to sub-agents/tools. Per-path guard keeps it finite (traces are acyclic).
function buildGraph(graph: TraceGraphData) {
  const nodes = Array.isArray(graph?.nodes) ? graph.nodes : [];
  const edges = Array.isArray(graph?.edges) ? graph.edges : [];
  const byId: Record<string, TGNode> = {};
  nodes.forEach((n) => { byId[n.id] = n; });
  const childrenOf: Record<string, TGEdge[]> = {};
  edges.forEach((e) => { (childrenOf[e.source] = childrenOf[e.source] || []).push(e); });
  const indeg: Record<string, number> = {};
  edges.forEach((e) => { indeg[e.target] = (indeg[e.target] || 0) + 1; });
  let roots = nodes.filter((n) => !indeg[n.id]);
  if (!roots.length && nodes.length) {
    roots = [nodes.reduce((a, b) => ((a.level ?? 0) <= (b.level ?? 0) ? a : b))];
  }

  const rfNodes: Node[] = [];
  const rfEdges: Edge[] = [];
  const adj: Record<string, { nodes: Set<string>; edges: Set<string> }> = {};
  const ensure = (id: string) => (adj[id] || (adj[id] = { nodes: new Set(), edges: new Set() }));
  let counter = 0;

  const walk = (origId: string, instId: string, denied: boolean, seen: Set<string>) => {
    const orig = byId[origId];
    if (!orig) return;
    rfNodes.push({
      id: instId,
      type: "traceNode",
      position: { x: 0, y: 0 },
      style: { width: estWidth(orig.label) },
      data: { label: orig.label, sublabel: orig.sublabel, nodeType: orig.type, denied },
      draggable: true,
      connectable: false,
    } as Node);
    (childrenOf[origId] || []).forEach((e) => {
      if (seen.has(e.target)) return; // acyclic guard
      const childInst = `n${counter++}`;
      const eid = `e${counter++}`;
      const deny = e.decision === "DENY";
      const color = deny ? "#f87171" : "rgba(123,179,255,0.8)";
      rfEdges.push({
        id: eid,
        source: instId,
        target: childInst,
        style: { stroke: color, strokeWidth: 2, strokeDasharray: deny ? "5 3" : undefined },
        markerEnd: { type: MarkerType.ArrowClosed, color, width: 8, height: 8 },
      } as Edge);
      ensure(instId).nodes.add(childInst);
      ensure(childInst).nodes.add(instId);
      ensure(instId).edges.add(eid);
      ensure(childInst).edges.add(eid);
      walk(e.target, childInst, deny, new Set(seen).add(e.target));
    });
  };

  roots.forEach((r) => {
    walk(r.id, `n${counter++}`, false, new Set([r.id]));
  });

  return { rfNodes: layoutNodes(rfNodes, rfEdges), rfEdges, adj };
}

// A render error in React Flow must never take down the whole audit page — show a fallback instead.
class DagErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch() {
    /* swallowed — fallback UI below */
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2">
          <AlertTriangle className="w-7 h-7 text-amber-500" />
          <p className="text-sm">Couldn't render the graph for this trace.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function DagInner({ graph }: { graph: TraceGraphData }) {
  const base = React.useMemo(() => buildGraph(graph), [graph]);
  // Own the nodes/edges so React Flow can persist each node's measured size (via onNodesChange). Fully-controlled
  // props recomputed on hover forced a re-measure every time, which made the edges flash — the reported flicker.
  const [nodes, setNodes, onNodesChange] = useNodesState(base.rfNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(base.rfEdges);
  const [hoverId, setHoverId] = React.useState<string | null>(null);

  // Reload the graph only when a new trace arrives — never on hover.
  React.useEffect(() => {
    setNodes(base.rfNodes);
    setEdges(base.rfEdges);
    setHoverId(null);
  }, [base, setNodes, setEdges]);

  // Spotlight the hovered node's calls by mapping over the CURRENT (already-measured) nodes/edges and touching
  // only `style` — dimensions are preserved, so nodes never go unmeasured and the edges no longer flicker.
  React.useEffect(() => {
    const near = hoverId ? base.adj[hoverId] : null;
    setNodes((nds) =>
      nds.map((n) => {
        const lit = !near || n.id === hoverId || near.nodes.has(n.id);
        return { ...n, style: { ...(n.style || {}), opacity: lit ? 1 : 0.2, transition: "opacity 120ms" } } as Node;
      })
    );
    setEdges((eds) =>
      eds.map((e) => {
        const on = near ? near.edges.has(e.id) : false;
        return { ...e, style: { ...(e.style || {}), opacity: near ? (on ? 1 : 0.08) : 1, strokeWidth: near && on ? 2.4 : 2 } } as Edge;
      })
    );
  }, [hoverId, base, setNodes, setEdges]);

  if (!base.rfNodes.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <GitBranch className="w-8 h-8 mb-2 text-muted-foreground/60" />
        <p className="text-sm">No delegation steps to graph for this trace</p>
      </div>
    );
  }

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodeTypes={nodeTypes}
      fitView
      fitViewOptions={{ padding: 0.12, maxZoom: 1, minZoom: 0.72 }}
      minZoom={0.35}
      proOptions={{ hideAttribution: true }}
      nodesConnectable={false}
      edgesFocusable={false}
      onNodeMouseEnter={(_, n) => setHoverId(n.id)}
      onNodeMouseLeave={() => setHoverId(null)}
    >
      <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="rgba(148,163,184,0.15)" />
      <Controls showInteractive={false} />
    </ReactFlow>
  );
}

export default function GatewayTraceDag({
  traceId,
  open,
  onClose,
}: {
  traceId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const [graph, setGraph] = React.useState<TraceGraphData | null>(null);
  const [events, setEvents] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open || !traceId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setGraph(null);
    setEvents([]);
    // Graph (nodes/edges) drives the DAG; the flat trace events drive the per-hop cards. Fetch both
    // independently so one failing never blanks the whole view.
    Promise.allSettled([GatewayAuditApi.traceGraph(traceId), GatewayAuditApi.trace(traceId)]).then(
      ([g, ev]) => {
        if (cancelled) return;
        if (g.status === "fulfilled") setGraph(g.value || { nodes: [], edges: [], blocked: [] });
        else setError(apiError(g.reason) || "Failed to load the governance trail");
        if (ev.status === "fulfilled") {
          const v = ev.value;
          setEvents(Array.isArray(v) ? v : v?.content || v?.events || []);
        }
        setLoading(false);
      }
    );
    return () => { cancelled = true; };
  }, [open, traceId]);

  const blocked = Array.isArray(graph?.blocked) ? (graph!.blocked as TGBlocked[]) : [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="management-dialog ag-dialog ag-dialog-wide">
        <DialogHeader className="management-dialog-header">
          <div className="management-dialog-heading">
            <span className="management-dialog-icon" aria-hidden="true">
              <GitBranch />
            </span>
            <div>
              <DialogTitle>Governance Trail — Delegation Chain</DialogTitle>
              <DialogDescription>
                Who delegated to whom, which tools ran, and what the PDP blocked
                {traceId ? (
                  <> — trace <code className="text-primary font-mono break-all">{traceId}</code></>
                ) : null}
                .
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="management-dialog-body">
          {blocked.length > 0 && (
            <div className="space-y-2 max-h-[22vh] overflow-y-auto">
              {blocked.map((b, i) => (
                <div key={i} className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2">
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500/20 text-red-600 dark:text-red-300 font-semibold">
                      <ShieldX className="w-3 h-3" /> BLOCKED
                    </span>
                    <span className="font-semibold text-foreground/90 break-all">{b.from} → {b.capability}</span>
                  </div>
                  <div className="text-[11px] text-red-600 dark:text-red-400 mt-1 break-words">
                    {b.reason || (b.policyId ? `Denied by policy [${b.policyId}]` : "Denied by policy")}
                  </div>
                </div>
              ))}
            </div>
          )}

          <style>{`
            .gw-trace-dag .react-flow__controls{box-shadow:0 1px 3px rgb(15 23 42 / 0.08);border:1px solid hsl(var(--border));border-radius:8px;overflow:hidden}
            .gw-trace-dag .react-flow__controls-button{background:hsl(var(--card));border-bottom:1px solid hsl(var(--border))}
            .gw-trace-dag .react-flow__controls-button:hover{background:hsl(var(--primary) / 0.06)}
            .gw-trace-dag .react-flow__controls-button svg{fill:hsl(var(--muted-foreground))}
          `}</style>
          <div className="gw-trace-dag h-[52vh] rounded-xl border border-border overflow-hidden ag-surface">
            {loading ? (
              <div className="flex items-center justify-center h-full text-sm text-muted-foreground">Loading governance trail…</div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2 px-6 text-center">
                <AlertTriangle className="w-7 h-7 text-amber-500" />
                <p className="text-sm">Couldn't load the governance trail.</p>
                <p className="text-[11px] text-muted-foreground/70 break-words max-w-md">{error}</p>
              </div>
            ) : graph ? (
              <DagErrorBoundary>
                <ReactFlowProvider>
                  <DagInner graph={graph} />
                </ReactFlowProvider>
              </DagErrorBoundary>
            ) : null}
          </div>

          <div className="flex items-center gap-4 text-[11px] text-muted-foreground pt-1">
            <span className="flex items-center gap-1">
              <span className="w-3 h-0.5 inline-block" style={{ background: "rgba(123,179,255,0.7)" }} /> allowed
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-0.5 inline-block bg-red-400" /> denied
            </span>
            <span className="ml-auto">Hover a node to spotlight its calls</span>
          </div>

          {!loading && !error && events.length > 0 && (
            <div className="pt-1">
              <GatewayConsoleHops events={events} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
